import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAlerts, snoozeAlert, uploadCocReceipt } from "../api.js";
import "/src/styles/AlertsPanel.css";

/* ----------------------- tiny inline icons (16px) ----------------------- */
const Icon = {
  Bell: (cls = "") => (
    <svg className={`w-4 h-4 ${cls}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0 1 18 14.158V11a6 6 0 0 0-12 0v3.158c0 .538-.214 1.055-.595 1.437L4 17h5m2 4a3 3 0 0 0 3-3H9a3 3 0 0 0 3 3z"
      />
    </svg>
  ),
  X: (cls = "") => (
    <svg className={`w-3.5 h-3.5 ${cls}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
  Warn: (cls = "") => (
    <svg className={`w-4 h-4 ${cls}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="2" strokeLinejoin="round" d="M12.9 3.6l8.1 14.1a1.5 1.5 0 0 1-1.3 2.3H4.3a1.5 1.5 0 0 1-1.3-2.3L11.1 3.6a1.5 1.5 0 0 1 1.8 0z" />
      <path strokeWidth="2" strokeLinecap="round" d="M12 9v4" />
      <circle cx="12" cy="16.5" r="1" fill="currentColor" />
    </svg>
  ),
  Clock: (cls = "") => (
    <svg className={`w-4 h-4 ${cls}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <circle cx="12" cy="12" r="9" strokeWidth="2" />
      <path strokeWidth="2" strokeLinecap="round" d="M12 7v5l3 2" />
    </svg>
  ),
  Doc: (cls = "") => (
    <svg className={`w-4 h-4 ${cls}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="2" d="M7 3h7l5 5v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
      <path strokeWidth="2" d="M14 3v5h5" />
    </svg>
  ),
  License: (cls = "") => (
    <svg className={`w-4 h-4 ${cls}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="3" y="5" width="18" height="14" rx="2" strokeWidth="2" />
      <path strokeWidth="2" d="M7 9h6M7 13h10" />
    </svg>
  ),
  Tax: (cls = "") => (
    <svg className={`w-4 h-4 ${cls}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <circle cx="8.5" cy="8.5" r="2.5" strokeWidth="2" />
      <circle cx="15.5" cy="15.5" r="2.5" strokeWidth="2" />
      <path strokeWidth="2" d="M7 17l10-10" />
    </svg>
  ),
  Refresh: (cls = "") => (
    <svg className={`w-3.5 h-3.5 ${cls}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.6M20 12a8 8 0 0 0-15.4-2M20 20v-5h-.6M4.1 13a8 8 0 0 0 15.3 2" />
    </svg>
  ),
  Check: (cls = "") => (
    <svg className={`w-4 h-4 ${cls}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
};

/* ----------------------- helpers ----------------------- */
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const daysDiffFromToday = (iso) => {
  if (!iso) return null;
  const today = startOfDay(new Date());
  const date = startOfDay(new Date(iso));
  const ms = date - today;
  return Math.round(ms / 86400000);
};

const dueWord = (days) => {
  if (days === null) return "";
  if (days < 0) return `Overdue ${Math.abs(days)}d`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `Due in ${days}d`;
};

const expiresWord = (days) => {
  if (days === null) return "";
  if (days < 0) return `Expired ${Math.abs(days)}d ago`;
  if (days === 0) return "Expires today";
  if (days === 1) return "Expires tomorrow";
  return `Expires in ${days}d`;
};

const toneFromDays = (days) => {
  if (days === null) return "info";
  if (days <= 0) return "critical";
  if (days <= 7) return "warning";
  return "info";
};

const first = (...v) => v.find((x) => x !== undefined && x !== null && x !== "");

const byCompany = (rows) => {
  const map = new Map();
  for (const r of rows) {
    const key = r.company || "—";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([company, items]) => ({ company, items }));
};

/* ----------------------- row building from backend payload ----------------------- */
function buildRows(p) {
  if (!p) return { count: 0, rows: [], stats: {} };
  const t = p.totals || {};
  const count =
    (t.upcomingReviews || 0) +
    (t.cocFeesDue || 0) +
    (t.expiringDocs || 0) +
    (t.pendingDocs || 0) +
    (t.deviezenExpiring || 0) +
    (t.businessLicensesExpiring || 0) +
    (t.taxFilingsDue || 0);

  const rows = [];

  // Build stats for summary
  const stats = {
    critical: 0,
    warning: 0,
    info: 0,
  };

  // CoC fees
  (p.cocFeesDue || []).forEach((c) => {
    const days = daysDiffFromToday(c.coc_fee_paid_through);
    const label = c.coc_fee_paid_through ? dueWord(days) : "Overdue • No payment on record";
    const tone = c.coc_fee_paid_through ? toneFromDays(days) : "critical";
    stats[tone]++;

    rows.push({
      id: `coc-${c.company_id}`,
      company_id: c.company_id,
      company: c.company_name,
      icon: "warn",
      tone,
      title: "CoC fee due",
      meta: c.coc_fee_paid_through ? `${label} • Through ${fmtDate(c.coc_fee_paid_through)}` : label,
      actions: ["Upload Proof", "Snooze"],
      _kind: "coc",
    });
  });

  // Upcoming reviews
  (p.upcomingReviews || []).forEach((r) => {
    const d = r.next_review_date || r.review_date;
    const days = daysDiffFromToday(d);
    const tone = toneFromDays(days);
    stats[tone]++;

    rows.push({
      id: `rev-${first(r.id, r.company_id)}`,
      company_id: r.company_id,
      company: r.company_name,
      icon: "clock",
      tone,
      title: "Review due",
      meta: `${dueWord(days)} • ${fmtDate(d)}`,
      actions: ["Open", "Snooze"],
      _kind: "review",
    });
  });

  // Expiring documents
  (p.expiringDocs || []).forEach((d) => {
    const days = daysDiffFromToday(d.expires_on);
    const tone = toneFromDays(days);
    stats[tone]++;
    const kind = (d.kind || "Document").replace(/_/g, " ");
    const ident = d.identifier ? ` (${d.identifier})` : "";

    rows.push({
      id: `doc-exp-${d.id}`,
      company_id: d.company_id,
      company: d.company_name,
      icon: "doc",
      tone,
      title: `${capitalize(kind)}${ident}`,
      meta: `${expiresWord(days)} • ${fmtDate(d.expires_on)}`,
      actions: ["Open", "Snooze"],
      _kind: "doc",
      _doc: { id: d.id, kind: d.kind, identifier: d.identifier },
    });
  });

  // Pending documents
  (p.pendingDocs || []).forEach((d) => {
    stats.info++;
    rows.push({
      id: `doc-pend-${d.id}`,
      company_id: d.company_id,
      company: d.company_name,
      icon: "doc",
      tone: "info",
      title: `Document pending${d.identifier ? ` (${d.identifier})` : ""}`,
      meta: `Requested ${fmtDate(d.created_at)}`,
      actions: ["Open", "Snooze"],
      _kind: "doc",
      _doc: { id: d.id, kind: d.kind, identifier: d.identifier },
    });
  });

  // FX permits
  (p.deviezenExpiring || []).forEach((x) => {
    const days = daysDiffFromToday(x.expires_on);
    const tone = toneFromDays(days);
    stats[tone]++;
    const ident = x.permit_no ? ` (${x.permit_no})` : "";
    const kind = x.kind ? ` ${capitalize(x.kind)}` : "";

    rows.push({
      id: `fx-${x.id}`,
      company_id: x.company_id,
      company: x.company_name,
      icon: "license",
      tone,
      title: `FX permit${kind}${ident}`,
      meta: `${expiresWord(days)} • ${fmtDate(x.expires_on)}`,
      actions: ["Open", "Snooze"],
      _kind: "fx",
    });
  });

  // Business licenses
  (p.businessLicensesExpiring || []).forEach((b) => {
    const days = daysDiffFromToday(b.business_license_expires_on);
    const tone = toneFromDays(days);
    stats[tone]++;
    const ident = b.business_license_no ? ` (${b.business_license_no})` : "";

    rows.push({
      id: `lic-${b.company_id}`,
      company_id: b.company_id,
      company: b.company_name,
      icon: "license",
      tone,
      title: `Business license${ident}`,
      meta: `${expiresWord(days)} • ${fmtDate(b.business_license_expires_on)}`,
      actions: ["Open", "Snooze"],
      _kind: "license",
    });
  });

  // Tax filings
  (p.taxFilingsDue || []).forEach((t) => {
    stats.critical++;
    rows.push({
      id: `tax-${t.id}`,
      company_id: t.company_id,
      company: t.company_name,
      icon: "tax",
      tone: "critical",
      title: `Tax filings due`,
      meta: `FY ${t.fiscal_year}`,
      actions: ["Open", "Snooze"],
      _kind: "tax",
    });
  });

  // Sort: critical → warning → info
  const rank = { critical: 0, warning: 1, info: 2 };
  rows.sort((a, b) => (rank[a.tone] ?? 9) - (rank[b.tone] ?? 9));

  return { count, rows, stats };
}

function capitalize(s = "") {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ----------------------- CoC Upload Proof Modal ----------------------- */
function CocUploadModal({ open, onClose, companyId, companyName, onUploaded }) {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [paidBy, setPaidBy] = useState("we");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    try {
      setSaving(true);
      await uploadCocReceipt(companyId, file, { notes, paidBy });
      onUploaded?.();
      onClose();
      setFile(null);
      setNotes("");
      setPaidBy("we");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to upload receipt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ap-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ap-modal-header">
          <div className="ap-modal-title">Upload CoC Payment Proof</div>
          <button className="ap-modal-close" onClick={onClose} type="button" aria-label="Close">
            {Icon.X()}
          </button>
        </div>

        <form className="ap-modal-body" onSubmit={onSubmit}>
          <div className="ap-field">
            <label>Company</label>
            <div className="ap-field-static">
              {companyName} (#{companyId})
            </div>
          </div>

          <div className="ap-field">
            <label>Receipt / Confirmation</label>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} required />
          </div>

          <div className="ap-field">
            <label>Who paid?</label>
            <div className="ap-radio-row">
              <label>
                <input type="radio" name="paidBy" value="we" checked={paidBy === "we"} onChange={() => setPaidBy("we")} /> We paid it
              </label>
              <label>
                <input type="radio" name="paidBy" value="client" checked={paidBy === "client"} onChange={() => setPaidBy("client")} /> Client paid it
              </label>
            </div>
          </div>

          <div className="ap-field">
            <label>Notes (optional)</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any details about this payment…" />
          </div>

          <div className="ap-modal-actions">
            <button type="button" className="alert-btn secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="alert-btn primary" disabled={!file || saving}>
              {saving ? "Saving…" : "Upload & Mark Paid"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ----------------------- Snooze Menu ----------------------- */
function SnoozeMenu({ alertId, position, onSnooze, onClose }) {
  const menuRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);

  const options = [
    { label: "1 hour", value: "1h" },
    { label: "4 hours", value: "4h" },
    { label: "1 day", value: "1d" },
    { label: "3 days", value: "3d" },
    { label: "1 week", value: "1w" },
    { label: "Until next review", value: "review" },
  ];

  return (
    <div ref={menuRef} className="snooze-dropdown" style={{ position: "fixed", left: position.x, top: position.y, zIndex: 60 }}>
      <div className="snooze-header">
        <span>Snooze alert</span>
      </div>
      {options.map((option) => (
        <button key={option.value} className="snooze-option" onClick={() => onSnooze(alertId, option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* ----------------------- Alert Item ----------------------- */
function AlertItem({ alert, onOpen, onSnooze }) {
  const handleAction = useCallback(
    (label, e) => {
      if (label === "Snooze" || label === "Later" || label === "Dismiss") {
        onSnooze(alert, e);
      } else {
        onOpen(alert);
      }
    },
    [alert, onOpen, onSnooze]
  );

  return (
    <div className="alert-item">
      <div className="alert-content">
        {/* Icon */}
        <div className="alert-icon">
          {alert.icon === "warn" && <div className="icon-circle yellow">{Icon.Warn()}</div>}
          {alert.icon === "clock" && <div className="icon-circle blue">{Icon.Clock()}</div>}
          {alert.icon === "doc" && <div className="icon-circle gray">{Icon.Doc()}</div>}
          {alert.icon === "license" && <div className="icon-circle green">{Icon.License()}</div>}
          {alert.icon === "tax" && <div className="icon-circle red">{Icon.Tax()}</div>}
        </div>

        {/* Main content */}
        <div className="alert-main">
          <div className="alert-title-row">
            <span className="alert-title">{alert.title}</span>
            {alert.tone === "warning" && <span className="badge warning">warning</span>}
            {alert.tone === "info" && <span className="badge info">info</span>}
            {alert.tone === "critical" && <span className="badge critical">critical</span>}
          </div>

          {alert.meta && <div className="alert-meta">{alert.meta}</div>}

          {/* Action buttons */}
          {Array.isArray(alert.actions) && alert.actions.length > 0 && (
            <div className="alert-actions">
              {alert.actions.slice(0, 2).map((label, idx) => {
                const isPrimary = idx === 0;
                return (
                  <button key={idx} type="button" className={`alert-btn ${isPrimary ? "primary" : "secondary"}`} onClick={(e) => handleAction(label, e)}>
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------- main component ----------------------- */
export default function AlertsPanel({ fetchAlerts }) {
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [payload, setPayload] = useState(null);
  const [snoozeMenu, setSnoozeMenu] = useState(null);
  const [cocModal, setCocModal] = useState({ open: false, companyId: null, companyName: "" });

  const navigate = useNavigate();
  const { count, rows, stats } = useMemo(() => buildRows(payload), [payload]);

  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const grouped = useMemo(() => byCompany(rows), [rows]);

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      if (typeof fetchAlerts === "function") {
        setPayload(await fetchAlerts(30));
      } else {
        const data = await getAlerts(30);
        setPayload(data);
      }
    } catch (e) {
      console.error("alerts fetch failed", e);
    } finally {
      setRefreshing(false);
    }
  }, [fetchAlerts]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;

    const onDown = (e) => {
      if (menuRef.current && btnRef.current && !menuRef.current.contains(e.target) && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleOpenAlert = useCallback(
    (alert) => {
      const id = alert.company_id;
      if (!id) return;

      if (alert._kind === "coc") {
        setCocModal({ open: true, companyId: id, companyName: alert.company });
        return;
      }

      const tab = alert._kind === "doc" ? "documents" : alert._kind === "review" ? "reviews" : alert._kind === "coc" ? "coc" : "overview";

      const to = `/company/${id}?tab=${tab}`;
      navigate(to, { state: { scrollTo: alert._kind === "doc" ? "documents" : tab } });
      setOpen(false);
    },
    [navigate]
  );

  const handleSnoozeClick = useCallback((alert, event) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setSnoozeMenu({ alertId: alert.id, x: rect.left, y: rect.bottom + 5 });
  }, []);

  const handleSnooze = useCallback(
    async (alertId, duration) => {
      try {
        await snoozeAlert(alertId, duration);
        setSnoozeMenu(null);
        await load();
      } catch (error) {
        console.error("Failed to snooze alert:", error);
        setSnoozeMenu(null);
      }
    },
    [load]
  );

  return (
    <div className="alerts-panel">
      {/* Trigger button */}
      <button ref={btnRef} type="button" onClick={() => setOpen((v) => !v)} className="alerts-trigger" aria-expanded={open} aria-controls="alerts-menu">
        <Icon.Bell />
        <span className="alerts-text">ALERTS</span>
        {count > 0 && <span className="alerts-count">{count}</span>}
      </button>

      {/* Dropdown menu */}
      {open && (
        <div ref={menuRef} id="alerts-menu" role="menu" className="alerts-dropdown">
          {/* Header */}
          <div className="alerts-header">
            <div className="alerts-title">
              <Icon.Bell />
              <span>Alerts</span>
              {refreshing && <Icon.Refresh className="alerts-loading" />}
            </div>

            {/* Stats summary */}
            {count > 0 && (
              <div className="alerts-stats">
                {stats.critical > 0 && <span className="stat-badge critical">{stats.critical} critical</span>}
                {stats.warning > 0 && <span className="stat-badge warning">{stats.warning} warning</span>}
                {stats.info > 0 && <span className="stat-badge info">{stats.info} info</span>}
              </div>
            )}

            <button onClick={() => setOpen(false)} className="alerts-close" title="Close" type="button">
              Close
            </button>
          </div>

          {/* Alerts list — grouped by company */}
          <div className="alerts-list">
            {grouped.length ? (
              grouped.map(({ company, items }) => (
                <div key={company} className="alerts-group">
                  <div className="alerts-group-header">
                    <div className="alerts-group-title">
                      <span className="company-name">{company}</span>
                      <span className="company-count">{items.length}</span>
                    </div>
                  </div>

                  {items.map((r) => (
                    <AlertItem key={r.id} alert={r} onOpen={handleOpenAlert} onSnooze={handleSnoozeClick} />
                  ))}
                </div>
              ))
            ) : (
              <div className="no-alerts">
                <Icon.Check className="check-icon" />
                <div>All caught up!</div>
                <div className="no-alerts-sub">No pending alerts</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Snooze menu */}
      {snoozeMenu && <SnoozeMenu alertId={snoozeMenu.alertId} position={{ x: snoozeMenu.x, y: snoozeMenu.y }} onSnooze={handleSnooze} onClose={() => setSnoozeMenu(null)} />}

      {/* CoC upload modal */}
      <CocUploadModal
        open={cocModal.open}
        companyId={cocModal.companyId}
        companyName={cocModal.companyName}
        onUploaded={load}
        onClose={() => setCocModal({ open: false, companyId: null, companyName: "" })}
      />
    </div>
  );
}
